import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CONNECT4_COLS,
  CONNECT4_ROWS,
  deriveConnect4,
  isGuestId,
  replayStates,
  type Connect4Board,
  type Connect4Move,
  type GameMeta,
} from '@awwyeah/shared';
import { GameHeader } from './components/GameHeader';
import { SideLine } from './components/SideLine';
import { ReplayControls, ReplayEntryButtons } from './components/ReplayControls';
import { canPlayMove, createGameMeta, nowIso } from './gameOps';
import { initialScrubState, scrubReducer } from './replayState';
import { useConnect4Moves } from './useGameMoves';
import { useGamesRegistry } from './useGamesRegistry';
import { useGameRoster } from './useHouseholdRoster';
import { SoundToggle } from './components/SoundToggle';
import { buzz, chime, noise, tone, unlockGameAudio } from './synth';
import { publishRematch } from './rematch';

/** Playback cadence for "Watch replay" — one move drop every ~0.8s, matched to the existing
 * piece-drop animation so a replayed game reads as the same motion as playing it live. */
const REPLAY_TICK_MS = 800;

/** How long a disc takes to fall to `row` (0 = top) and settle — deeper falls take longer. */
function dropMs(row: number): number {
  return 300 + row * 55;
}
/** Fraction of the drop animation at which the disc first hits bottom (matches the keyframes). */
const IMPACT_AT = 0.6;

/** The plastic clack of a disc landing; `soft` is the little rattle as it settles. Lower in
 * the frame (more stack under it) rings a touch lower. */
function playDiscClack(row: number, soft = false): void {
  const gain = soft ? 0.05 : 0.2;
  const dur = soft ? 0.04 : 0.07;
  noise({ dur, gain, filter: 2700 - row * 110, q: 3 });
  tone({ freq: (soft ? 1500 : 980) - row * 35, to: 420, type: 'triangle', dur, gain: gain * 0.8 });
}

/** Classic blue plastic frame — the object, fixed in every theme (like Pool's felt). */
const FRAME: React.CSSProperties = {
  backgroundColor: '#1d5cc4',
  backgroundImage: 'linear-gradient(180deg, #3d7fe4 0%, #1f5fc8 38%, #17489f 100%)',
  boxShadow:
    '0 12px 24px rgba(10,30,80,.35), inset 0 2px 0 rgba(255,255,255,.38), inset 0 -3px 0 rgba(0,0,0,.28)',
};

const CELL_SIZE = `calc(100% / ${CONNECT4_COLS}) calc(100% / ${CONNECT4_ROWS})`;
const HOLES = 'radial-gradient(circle closest-side, transparent 81%, #000 83.5%)';

/** The face of the frame laid over the discs, with a hole per cell (CSS mask) and a soft
 * shadow ring around each hole so it reads as moulded plastic, not a flat sticker. */
const FACE: React.CSSProperties = {
  backgroundImage: [
    'radial-gradient(circle closest-side, rgba(5,20,60,.55) 80%, rgba(5,20,60,0) 95%)',
    'linear-gradient(180deg, #3577de 0%, #1f5fc8 45%, #1a51ad 100%)',
  ].join(','),
  backgroundSize: `${CELL_SIZE}, 100% 100%`,
  WebkitMaskImage: HOLES,
  WebkitMaskSize: CELL_SIZE,
  maskImage: HOLES,
  maskSize: CELL_SIZE,
};

const FOOT: React.CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, #17489f, #10357a)',
  boxShadow: '0 3px 5px rgba(0,0,0,.3)',
};

/** A chunky bevelled disc in a member colour, with the moulded ring real discs have. */
function discStyle(color: string): React.CSSProperties {
  return {
    backgroundColor: color,
    backgroundImage: [
      'radial-gradient(circle closest-side, transparent 58%, rgba(0,0,0,.2) 61%, rgba(255,255,255,.18) 66%, transparent 70%)',
      'radial-gradient(circle at 33% 27%, rgba(255,255,255,.7) 0 7%, rgba(255,255,255,0) 32%)',
      `radial-gradient(circle closest-side, ${color} 0 72%, color-mix(in srgb, ${color} 70%, black) 100%)`,
    ].join(','),
    boxShadow: 'inset 0 -3px 4px rgba(0,0,0,.28), inset 0 2px 3px rgba(255,255,255,.35)',
  };
}

const C4_KEYFRAMES = `
@keyframes awwyeah-c4-drop {
  0% { transform: translateY(var(--fall)); animation-timing-function: cubic-bezier(.55,0,1,.45) }
  60% { transform: translateY(0); animation-timing-function: cubic-bezier(0,.55,.45,1) }
  75% { transform: translateY(-13%); animation-timing-function: cubic-bezier(.55,0,1,.45) }
  88% { transform: translateY(0); animation-timing-function: cubic-bezier(0,.55,.45,1) }
  94% { transform: translateY(-3%) }
  100% { transform: translateY(0) }
}
@keyframes awwyeah-c4-hover { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-7%) } }
@keyframes awwyeah-c4-jolt { 0%,100% { transform: translateY(0) } 35% { transform: translateY(1.5px) } }
@keyframes awwyeah-c4-win { 0%,100% { transform: scale(1); filter: brightness(1) } 50% { transform: scale(.92); filter: brightness(1.35) } }
@keyframes awwyeah-c4-line { from { opacity: 0 } to { opacity: 1 } }
@keyframes awwyeah-c4-flash { 0% { transform: translate(-50%,-50%) scale(.6); opacity: 0 } 15% { transform: translate(-50%,-50%) scale(1.08); opacity: 1 } 80% { opacity: 1 } 100% { transform: translate(-50%,-60%) scale(1); opacity: 0 } }
@media (prefers-reduced-motion: reduce) { [data-kind="connect4"] * { animation: none !important } }
`;

/** One side of the table: a disc in their colour and their name. "You" sits nearest you. */
function PlayerTag({
  testId,
  name,
  color,
  active,
}: {
  testId: string;
  name: string;
  color: string;
  active: boolean;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      data-active={active}
      className={`flex h-9 items-center gap-2 rounded-full border px-2 text-sm transition-colors ${
        active ? 'bg-surface text-fg' : 'border-line text-fg-2'
      }`}
      style={active ? { borderColor: color } : undefined}
    >
      <span className="h-6 w-6 shrink-0 rounded-full" style={discStyle(color)} aria-hidden="true" />
      <span className="truncate font-semibold">{name}</span>
      {active && <span className="ml-auto shrink-0 pr-1 text-xs text-fg-3">to play</span>}
    </div>
  );
}

/**
 * Connect-4 board (WS-17a; replay + history WS-17b; split out of `GameBoardPage` in WS-17c
 * when Battleship gave the route a second kind to dispatch to). Tap a column to drop; the
 * client validates before pushing (`canPlayMove`), but the append-only move log +
 * `deriveConnect4` replay is the real authority — a rejected/duplicate/offline-race move
 * never corrupts the board, it just doesn't count (see
 * `packages/shared/src/modules/games/connect4.ts`). Spectators (anyone not in `playerIds`)
 * get the same live board with no input.
 *
 * **Replay/history (WS-17b).** The board always renders one of two things: the live derived
 * state (`useConnect4Moves`'s `state` — for a finished game this already *is* the final
 * position, so opening a finished game "just works" with no special-casing), or a specific
 * frame from `replayStates` while `scrub.mode === 'scrubbing'` (`replayState.ts`'s state
 * machine). Scrubbing NEVER writes — it only changes which frame is rendered — so a
 * spectator or a player mid-history-scrub can never accidentally corrupt the move log.
 */
export function Connect4Game({ meta, userId }: { meta: GameMeta; userId: string }): JSX.Element {
  const navigate = useNavigate();
  const registries = useGamesRegistry();
  const roster = useGameRoster(meta.guestNames);

  const playerIds = meta.playerIds;
  const { movesArray, moves, state } = useConnect4Moves(meta.id, playerIds);

  // The replay engine (`@awwyeah/shared`'s `replay.ts`) turns the same move log into one
  // frame per prefix length — Connect-4 plugs in `deriveConnect4` bound to this game's
  // `playerIds`; Battleship plugs in `deriveBattleship` and gets the identical controls.
  // `frames.at(-1).state` always equals `state` above (same input, same derive).
  const derive = useCallback(
    (prefix: readonly Connect4Move[]) => deriveConnect4(prefix, playerIds),
    [playerIds],
  );
  const frames = useMemo(() => replayStates(moves, derive), [moves, derive]);

  const [scrub, dispatchScrub] = useReducer(scrubReducer, initialScrubState);

  // A live move landing while scrubbing never yanks the view back — it only surfaces the
  // "game moved on — back to live?" chip (see replayState.ts's doc comment). Skipped on the
  // very first render (a mount isn't "a move arriving").
  const prevMoveCountRef = useRef(moves.length);
  useEffect(() => {
    if (moves.length > prevMoveCountRef.current) dispatchScrub({ type: 'new-move-arrived' });
    prevMoveCountRef.current = moves.length;
  }, [moves.length]);

  // "Watch replay" playback — one tick every REPLAY_TICK_MS while `scrub.playing`, matched
  // to the drop animation below so replaying reads as the same motion as playing it live.
  useEffect(() => {
    if (scrub.mode !== 'scrubbing' || !scrub.playing) return undefined;
    const timer = setTimeout(
      () => dispatchScrub({ type: 'tick', maxFrame: frames.length - 1 }),
      REPLAY_TICK_MS,
    );
    return () => clearTimeout(timer);
  }, [scrub, frames.length]);

  const displayedState = scrub.mode === 'scrubbing' ? (frames[scrub.frame]?.state ?? state) : state;

  // ---- Drop animation. A single new disc (one move landing, live or in replay) falls from
  // the rail above its column and bounce-settles; anything bigger (opening the game, jumping
  // around history) just snaps. The clack plays on the moment of impact, not the tap.
  // Keyed on the board's *contents*, and the timers outlive re-renders (a registry write or
  // a re-derive hands us a new board object with the same discs — that mustn't cut a drop).
  const boardKey = displayedState.board.map((r) => r.map((c) => c ?? '.').join('')).join('/');
  const prevBoardRef = useRef<Connect4Board>(displayedState.board);
  const [dropping, setDropping] = useState<{ key: string; row: number } | null>(null);
  const [jolt, setJolt] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const fxTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const later = (fn: () => void, ms: number): void => {
    fxTimersRef.current.push(setTimeout(fn, ms));
  };
  useEffect(() => () => fxTimersRef.current.forEach(clearTimeout), []);

  useEffect(() => {
    const prev = prevBoardRef.current;
    const board = displayedState.board;
    prevBoardRef.current = board;
    const fresh: { key: string; row: number }[] = [];
    for (let r = 0; r < CONNECT4_ROWS; r += 1) {
      for (let c = 0; c < CONNECT4_COLS; c += 1) {
        if (prev[r]?.[c] == null && board[r]?.[c] != null) fresh.push({ key: `${r}-${c}`, row: r });
      }
    }
    fxTimersRef.current.forEach(clearTimeout);
    fxTimersRef.current = [];
    setJolt(false);
    const one = fresh.length === 1 ? fresh[0] : undefined;
    setDropping(one ?? null);
    if (!one) return;
    const total = dropMs(one.row);
    later(() => {
      playDiscClack(one.row);
      buzz(14);
      setJolt(true);
    }, total * IMPACT_AT);
    later(() => playDiscClack(one.row, true), total * 0.88);
    later(() => {
      setDropping(null);
      setJolt(false);
    }, total + 40);

    // "Four in a row!" — only when a win happens on screen, never on opening a won game.
    const win = displayedState.winner;
    const winnerWasThere =
      prev.length > 0 && win !== null && win.cells.every(([r, c]) => prev[r]?.[c] != null);
    if (win && !winnerWasThere) {
      const text =
        meta.playerIds[win.playerIndex] === userId ? 'Four in a row — you win!' : 'Four in a row!';
      later(() => {
        setFlash(text);
        chime([523, 659, 784, 1047], 0.09);
        buzz([20, 60, 30]);
      }, total + 60);
      later(() => setFlash(null), total + 1860);
    }
    // Contents-keyed on purpose (see above).
  }, [boardKey]);
  // ---- Aim preview: the column under your finger/pointer gets a ghost disc on the rail and
  // a dashed ring where it would land. Tapping still drops in one go.
  const [aimCol, setAimCol] = useState<number | null>(null);

  // Once the derived board says the match is decided, stamp the registry meta so the Apps
  // tab's quiet "Finished" section (no win/loss record, just a fact) picks it up — the
  // move log itself is already the source of truth for *whether* it's over; this only
  // mirrors that fact onto `GameMeta.status`/`finishedAt` for the list/archive view. Gated
  // to players so a spectator's client, which never writes anything, can't race this — and
  // to non-guests, because `games` is the one doc a guest may never open (WS-23, see
  // `apps/server/src/modules/games/guest/access.ts`); a guest winner leaves the stamp to
  // the household member's own client instead.
  useEffect(() => {
    if (!meta.playerIds.includes(userId) || isGuestId(userId)) return;
    if (meta.status === 'done') return;
    if (!state.winner && !state.isDraw) return;
    const winnerId = state.winner ? meta.playerIds[state.winner.playerIndex] : undefined;
    const at = nowIso();
    registries.map.set(meta.id, {
      ...meta,
      status: 'done',
      ...(winnerId ? { winnerId } : {}),
      finishedAt: at,
      updatedBy: userId,
      updatedAt: at,
    });
  }, [meta, userId, registries.map, state.winner, state.isDraw]);

  const isPlayer = meta.playerIds.includes(userId);
  // Rematch creates a *new* registry entry, and the `games` doc is the one thing a guest may
  // not open (WS-23) — so the button is theirs to press only if they're a member. A guest who
  // wants another game asks for another link.
  const canRematch = isPlayer && !isGuestId(userId);
  const opponentId = meta.playerIds.find((id) => id !== userId) ?? meta.playerIds[1];
  const opponent = roster.getMember(opponentId);
  const finished = state.winner !== null || state.isDraw;
  const myTurn = isPlayer && state.turn !== null && meta.playerIds[state.turn] === userId;

  const statusLine = (): string => {
    if (state.winner) {
      const winnerId = meta.playerIds[state.winner.playerIndex];
      if (!isPlayer) return `${roster.getMember(winnerId).name} won`;
      return winnerId === userId ? 'You won' : `${opponent.name} won`;
    }
    if (state.isDraw) return 'Draw';
    if (state.turn === null) return '';
    const turnId = meta.playerIds[state.turn];
    if (!isPlayer) return `${roster.getMember(turnId).name}'s move`;
    return turnId === userId ? 'Your move' : `${opponent.name}'s move`;
  };

  const nearIndex: 0 | 1 = meta.playerIds[1] === userId ? 1 : 0;
  const farIndex: 0 | 1 = nearIndex === 0 ? 1 : 0;
  const canAim = isPlayer && !finished && myTurn && scrub.mode === 'live';
  const landingRow = (col: number): number | null => {
    for (let r = CONNECT4_ROWS - 1; r >= 0; r -= 1) {
      if (state.board[r]?.[col] == null) return r;
    }
    return null;
  };
  const winCells = displayedState.winner?.cells ?? [];
  const firstWin = winCells[0];
  const lastWin = winCells[winCells.length - 1];
  const winLine =
    firstWin && lastWin
      ? { x1: firstWin[1] + 0.5, y1: firstWin[0] + 0.5, x2: lastWin[1] + 0.5, y2: lastWin[0] + 0.5 }
      : null;
  const winningCells = new Set((displayedState.winner?.cells ?? []).map(([r, c]) => `${r}-${c}`));
  const maxFrame = frames.length - 1;
  const frameAt = frames[scrub.frame];

  const handleDrop = (col: number): void => {
    if (!isPlayer || finished) return;
    if (scrub.mode === 'scrubbing') return; // view-only while scrubbing/replaying
    if (!canPlayMove(moves, meta.playerIds, userId, col)) return;
    setAimCol(null);
    movesArray.push([{ col, playerId: userId, at: nowIso() }]);
  };

  const rematch = (): void => {
    const fresh = createGameMeta({
      kind: meta.kind,
      starterId: userId,
      starterName: roster.getMember(userId).name,
      opponentId,
      opponentName: opponent.name,
    });
    publishRematch(registries.map, fresh, meta);
    navigate(`/games/${fresh.id}`);
  };

  const colorFor = (playerIndex: 0 | 1): string =>
    roster.getMember(meta.playerIds[playerIndex]).color;

  return (
    <div className="flex flex-col gap-4 py-4" data-testid="game-board-page" data-kind="connect4">
      <style>{C4_KEYFRAMES}</style>
      <GameHeader
        title={meta.title}
        status={statusLine()}
        spectating={!isPlayer}
        myTurn={myTurn}
        finished={finished}
      >
        <ReplayEntryButtons
          canWatchReplay={finished && scrub.mode === 'live' && moves.length > 0}
          canSeeHistory={!finished && scrub.mode === 'live' && moves.length > 0}
          onWatchReplay={() => {
            dispatchScrub({ type: 'start-scrub', frame: 0 });
            dispatchScrub({ type: 'play' });
          }}
          onSeeHistory={() => dispatchScrub({ type: 'start-scrub', frame: maxFrame })}
        />
      </GameHeader>

      {isPlayer && (
        <SideLine
          color={roster.getMember(userId).color}
          turnText={statusLine()}
          turnColor={!finished && state.turn !== null ? colorFor(state.turn) : null}
          myTurn={myTurn}
        />
      )}

      {scrub.mode === 'scrubbing' && (
        <ReplayControls
          scrub={scrub}
          dispatch={dispatchScrub}
          maxFrame={maxFrame}
          finished={finished}
          positionLabel={
            scrub.frame === 0
              ? 'Start'
              : `Move ${scrub.frame} of ${maxFrame}${frameAt?.at ? ` · ${new Date(frameAt.at).toLocaleString()}` : ''}`
          }
        />
      )}

      <div className="mx-auto flex w-full max-w-[26rem] flex-col gap-1.5">
        {/* Across the table: the other player's tag sits at the far edge of the board. */}
        <PlayerTag
          testId="connect4-far-tag"
          name={isPlayer ? opponent.name : roster.getMember(meta.playerIds[1]).name}
          color={colorFor(isPlayer ? farIndex : 1)}
          active={!finished && state.turn === (isPlayer ? farIndex : 1)}
        />

        {/* The rail: where a held disc hovers before it drops. Same 7 tracks as the board. */}
        <div className="grid grid-cols-7 px-2.5" aria-hidden="true" data-testid="connect4-rail">
          {Array.from({ length: CONNECT4_COLS }, (_, col) => (
            <span key={col} className="relative aspect-square">
              {canAim && aimCol === col && landingRow(col) !== null && (
                <span
                  className="absolute inset-[7%] rounded-full"
                  style={{
                    ...discStyle(colorFor(nearIndex)),
                    animation: 'awwyeah-c4-hover 1.6s ease-in-out infinite',
                  }}
                />
              )}
            </span>
          ))}
        </div>

        <div
          className="relative rounded-[1.4rem] p-2.5"
          style={{
            ...FRAME,
            animation: jolt ? 'awwyeah-c4-jolt 180ms ease-out' : undefined,
          }}
          data-testid="connect4-board"
        >
          <div
            className="relative grid grid-cols-7 rounded-md"
            style={{ background: 'var(--app-bg)', boxShadow: 'inset 0 2px 8px rgba(0,0,0,.35)' }}
            onPointerLeave={() => setAimCol(null)}
          >
            {Array.from({ length: CONNECT4_COLS }, (_, col) => (
              <button
                key={col}
                type="button"
                data-testid="connect4-column"
                data-col={col}
                aria-label={`Drop in column ${col + 1}`}
                disabled={!canAim}
                onClick={() => {
                  unlockGameAudio();
                  handleDrop(col);
                }}
                onPointerEnter={() => canAim && setAimCol(col)}
                onPointerDown={() => canAim && setAimCol(col)}
                onFocus={() => canAim && setAimCol(col)}
                className="flex min-h-[44px] flex-col items-stretch outline-none disabled:cursor-default"
              >
                {Array.from({ length: CONNECT4_ROWS }, (_, row) => {
                  const cell = displayedState.board[row]?.[col] ?? null;
                  const key = `${row}-${col}`;
                  const isWinning = winningCells.has(key);
                  const isDropping = dropping?.key === key;
                  const isLanding = canAim && aimCol === col && landingRow(col) === row;
                  return (
                    <span
                      key={row}
                      data-testid="connect4-cell"
                      data-row={row}
                      data-col={col}
                      data-occupant={cell === null ? '' : cell}
                      data-winning={isWinning}
                      className="relative aspect-square w-full"
                    >
                      {cell !== null && (
                        <span
                          className="absolute inset-[7%] rounded-full"
                          style={{
                            ...discStyle(colorFor(cell)),
                            ['--fall' as string]: `${(-(row + 1.25) / 0.86) * 100}%`,
                            animation: isDropping
                              ? `awwyeah-c4-drop ${dropMs(row)}ms both`
                              : isWinning && scrub.mode === 'live'
                                ? 'awwyeah-c4-win 1.2s ease-in-out 3'
                                : undefined,
                            filter:
                              displayedState.winner && !isWinning
                                ? 'saturate(.55) brightness(.85)'
                                : undefined,
                          }}
                        />
                      )}
                      {isLanding && (
                        <span
                          className="absolute inset-[12%] rounded-full border-2 border-dashed"
                          style={{ borderColor: colorFor(nearIndex), opacity: 0.85 }}
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  );
                })}
              </button>
            ))}

            {/* The plastic face, drawn over the discs with round holes punched through it —
                so a falling disc really does slide down *behind* the frame. */}
            <span
              className="pointer-events-none absolute inset-0 rounded-md"
              style={FACE}
              aria-hidden="true"
            />

            {winLine && !dropping && (
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full"
                viewBox={`0 0 ${CONNECT4_COLS} ${CONNECT4_ROWS}`}
                preserveAspectRatio="none"
                aria-hidden="true"
                data-testid="connect4-win-line"
              >
                <line
                  {...winLine}
                  stroke="rgba(255,255,255,.92)"
                  strokeWidth={7}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  style={{
                    filter: 'drop-shadow(0 0 4px rgba(255,255,255,.8))',
                    animation: 'awwyeah-c4-line 600ms ease-out both',
                  }}
                />
              </svg>
            )}

            {flash && (
              <div
                className="pointer-events-none absolute left-1/2 top-1/2 whitespace-nowrap rounded-full px-4 py-2 font-display text-lg font-semibold"
                style={{
                  background: 'rgba(255,248,225,.97)',
                  color: '#173a7a',
                  boxShadow: '0 6px 18px rgba(0,0,0,.35)',
                  animation: 'awwyeah-c4-flash 1800ms ease-out both',
                  transform: 'translate(-50%,-50%)',
                }}
                data-testid="connect4-flash"
              >
                {flash}
              </div>
            )}
          </div>
        </div>
        {/* Little feet, so it stands on the table rather than floating. */}
        <div className="-mt-1.5 flex justify-between px-4" aria-hidden="true">
          <span className="h-2.5 w-14 rounded-b-lg" style={FOOT} />
          <span className="h-2.5 w-14 rounded-b-lg" style={FOOT} />
        </div>

        {/* Your side of the table. */}
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <PlayerTag
              testId="connect4-near-tag"
              name={isPlayer ? 'You' : roster.getMember(meta.playerIds[0]).name}
              color={colorFor(isPlayer ? nearIndex : 0)}
              active={!finished && state.turn === (isPlayer ? nearIndex : 0)}
            />
          </div>
          <SoundToggle />
        </div>
      </div>

      {finished && canRematch && scrub.mode === 'live' && (
        <button
          type="button"
          data-testid="rematch-button"
          onClick={rematch}
          className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
        >
          Rematch
        </button>
      )}
    </div>
  );
}
